export type ShopProductStatus = 'pi' | 'requested' | 'inquiry';

export interface ShopCategory {
  id: string;
  nameAr: string;
  nameEn: string;
}

export interface ShopProduct {
  id: string;
  category: string;
  nameAr: string;
  nameEn: string;
  model: string;
  vendor: string;
  protocol: string[];
  blurbAr: string;
  image: string;
  status: ShopProductStatus;
  badgeAr: string;
  qtyLabel?: string;
  piRef?: string;
}

export interface ShopCatalog {
  updatedAt: string;
  brand: {
    nameAr: string;
    nameEn: string;
    whatsapp: string;
    whatsappDisplay: string;
    cityAr: string;
    noteAr: string;
  };
  categories: ShopCategory[];
  products: ShopProduct[];
}

export const shopCatalog: ShopCatalog = {
  updatedAt: '2026-10-11T00:00:00.000Z',
  brand: {
    nameAr: 'الشراع — المنزل الذكي',
    nameEn: 'Al-Shiraa Smart Home',
    whatsapp: '963933612861',
    whatsappDisplay: '+963 933 612861',
    cityAr: 'اللاذقية',
    noteAr: 'جميع الأصناف التي طلبناها من مصانع الصين — الطلب عبر واتساب فقط. السعر يُؤكد حسب التوفر.',
  },
  categories: [
    { id: 'lighting', nameAr: 'إضاءة', nameEn: 'Lighting' },
    { id: 'strips', nameAr: 'شرائط وكنترولر', nameEn: 'Strips & Controllers' },
    { id: 'switches', nameAr: 'مفاتيح ومقابس', nameEn: 'Switches & Sockets' },
    { id: 'sensors', nameAr: 'حساسات', nameEn: 'Sensors' },
    { id: 'motors', nameAr: 'ستائر', nameEn: 'Curtains' },
    { id: 'control', nameAr: 'بوابات وتحكم', nameEn: 'Gateways & Control' },
    { id: 'locks', nameAr: 'أقفال', nameEn: 'Locks' },
    { id: 'cleaning', nameAr: 'تنظيف', nameEn: 'Cleaning' },
    { id: 'panels', nameAr: 'لوحات وكاميرات', nameEn: 'Panels & Cameras' },
  ],
  products: [
    // —— Lighting (PI) ——
    {
      id: 'bsum-wifi-e27',
      category: 'lighting',
      nameAr: 'لمبة ذكية E27 WiFi RGBCW',
      nameEn: 'Tuya WiFi Smart Bulb E27',
      model: 'PS-014BB06-B02 · 10W',
      vendor: 'BSUMWELL',
      protocol: ['Tuya WiFi'],
      blurbAr: 'لمبة ألوان كاملة + أبيض قابل للتعديل — مسار Tuya للدخول السريع.',
      image: '/shop/products/bulb-wifi-e27.png',
      status: 'pi',
      badgeAr: 'على PI · 120 قطعة',
      qtyLabel: '120 pcs',
      piRef: '12026092402',
    },
    {
      id: 'bsum-zb-e27',
      category: 'lighting',
      nameAr: 'لمبة Zigbee E27 RGB+CCT',
      nameEn: 'Zigbee RGB+CCT Bulb E27',
      model: 'BSZAZ-BL01-RGB10W · 10W',
      vendor: 'BSUMWELL',
      protocol: ['Zigbee 3.0'],
      blurbAr: 'خط Zigbee للمنازل التي تعتمد بوابة محلية ومعرض متكامل.',
      image: '/shop/products/bulb-zigbee-e27.png',
      status: 'pi',
      badgeAr: 'على PI · 160 قطعة',
      qtyLabel: '160 pcs',
      piRef: '12026092402',
    },
    {
      id: 'gledo-e27',
      category: 'lighting',
      nameAr: 'لمبة Gledopto E27 متوافقة Hue',
      nameEn: 'Gledopto Zigbee Bulb E27',
      model: 'GL-B-008P',
      vendor: 'Gledopto',
      protocol: ['Zigbee 3.0', 'Hue-compatible'],
      blurbAr: 'بديل احترافي متوافق مع بوابات Hue وSmartThings وConBee.',
      image: '/shop/products/bulb-zigbee-e27.png',
      status: 'pi',
      badgeAr: 'على PI · 80 قطعة',
      qtyLabel: '80 pcs',
      piRef: 'GL10260906',
    },
    {
      id: 'gledo-gu10',
      category: 'lighting',
      nameAr: 'سبوت GU10 Zigbee',
      nameEn: 'Zigbee GU10 Spotlight',
      model: 'GL-S-006P · 30°',
      vendor: 'Gledopto',
      protocol: ['Zigbee 3.0', 'Hue-compatible'],
      blurbAr: 'إضاءة مركّزة للأسقف والجدران — مسار Zigbee للمنازل ~300 م².',
      image: '/shop/products/bulb-gu10.png',
      status: 'pi',
      badgeAr: 'على PI · 140 قطعة',
      qtyLabel: '140 pcs',
      piRef: 'GL10260906',
    },

    // —— Strips ——
    {
      id: 'bsum-5dm',
      category: 'strips',
      nameAr: 'كنترولر شريط BSUMWELL 5-in-1',
      nameEn: 'Zigbee Pro+ 5-in-1 Controller',
      model: 'BSW-5DM',
      vendor: 'BSUMWELL',
      protocol: ['Zigbee Pro+'],
      blurbAr: 'كنترولر شرائط مع بوابة الإضاءة على نفس طلبية BSUMWELL.',
      image: '/shop/products/led-controller.png',
      status: 'pi',
      badgeAr: 'على PI · 25 قطعة',
      qtyLabel: '25 pcs',
      piRef: '12026092402',
    },
    {
      id: 'gledo-c201',
      category: 'strips',
      nameAr: 'كنترولر Gledopto Pro+ 5-in-1',
      nameEn: 'Gledopto Zigbee Pro+ Controller',
      model: 'GL-C-201P · 12–24V',
      vendor: 'Gledopto',
      protocol: ['Zigbee Pro+'],
      blurbAr: 'كنترولر شريط احترافي متوافق مع ريموتات Pro.',
      image: '/shop/products/led-controller.png',
      status: 'pi',
      badgeAr: 'على PI · 25 قطعة',
      qtyLabel: '25 pcs',
      piRef: 'GL10260906',
    },
    {
      id: 'gledo-rc009',
      category: 'strips',
      nameAr: 'ريموت Gledopto 8 مناطق',
      nameEn: '8-Zone RGB+CCT Remote',
      model: 'GL-RC-009',
      vendor: 'Gledopto',
      protocol: ['2.4G RF'],
      blurbAr: 'ريموت لمس للتحكم بالمشاهد مع كنترولرات Pro.',
      image: '/shop/products/led-remote.png',
      status: 'pi',
      badgeAr: 'على PI · 15 قطعة',
      qtyLabel: '15 pcs',
      piRef: 'GL10260906',
    },
    {
      id: 'laimi-wb5',
      category: 'strips',
      nameAr: 'كنترولر Laimi WB5',
      nameEn: 'WiFi+BT 5-in-1 Controller',
      model: 'WB5',
      vendor: 'Laimi',
      protocol: ['WiFi+BT', 'Tuya'],
      blurbAr: 'مسار Tuya لشرائط RGB+CCT — أحد موديلي WiFi على PI.',
      image: '/shop/products/led-controller.png',
      status: 'pi',
      badgeAr: 'على PI · 25 قطعة',
      qtyLabel: '25 pcs',
      piRef: 'YLM260923-1',
    },
    {
      id: 'laimi-c51w',
      category: 'strips',
      nameAr: 'كنترولر Laimi C51W',
      nameEn: 'WiFi+BT 5-in-1 Controller',
      model: 'C51W',
      vendor: 'Laimi',
      protocol: ['WiFi+BT', 'Tuya'],
      blurbAr: 'بديل WiFi على نفس PI — نختار الأفضل بعد الاختبار.',
      image: '/shop/products/led-controller.png',
      status: 'pi',
      badgeAr: 'على PI · 25 قطعة',
      qtyLabel: '25 pcs',
      piRef: 'YLM260923-1',
    },
    {
      id: 'laimi-lm052',
      category: 'strips',
      nameAr: 'كنترولر Laimi Zigbee LM052',
      nameEn: 'Zigbee 5-in-1 Controller',
      model: 'LM052',
      vendor: 'Laimi',
      protocol: ['Zigbee'],
      blurbAr: 'مسار Zigbee لشرائط 12/24V مع منظومة البوابة.',
      image: '/shop/products/led-controller.png',
      status: 'pi',
      badgeAr: 'على PI · 25 قطعة',
      qtyLabel: '25 pcs',
      piRef: 'YLM260923-1',
    },
    {
      id: 'laimi-rc03',
      category: 'strips',
      nameAr: 'ريموت Laimi RC03',
      nameEn: '4-Zone RGBCCT RF Remote',
      model: 'RC03',
      vendor: 'Laimi',
      protocol: ['RF'],
      blurbAr: 'ريموت مرافق لكنترولرات الشرائط.',
      image: '/shop/products/led-remote.png',
      status: 'pi',
      badgeAr: 'على PI · 15 قطعة',
      qtyLabel: '15 pcs',
      piRef: 'YLM260923-1',
    },
    {
      id: 'delikay-smd',
      category: 'strips',
      nameAr: 'شريط LED RGBW 24V (SMD)',
      nameEn: 'Flexible RGBW LED Strip 24V',
      model: 'DL5050-24 · IP20 · 5م/رول',
      vendor: 'Delikay',
      protocol: ['مع كنترولر 5-in-1'],
      blurbAr: 'إضاءة مخفية للأسقف والمطابخ — ~450 م لأول مخزون 5 منازل.',
      image: '/shop/products/led-strip-smd.png',
      status: 'pi',
      badgeAr: 'على PI · 450 م',
      qtyLabel: '450 م',
      piRef: '0260924',
    },
    {
      id: 'delikay-cob',
      category: 'strips',
      nameAr: 'شريط LED COB RGBW 24V',
      nameEn: 'COB RGBW LED Strip 24V',
      model: 'DLCOB-24 · IP20 · 5م/رول',
      vendor: 'Delikay',
      protocol: ['مع كنترولر 5-in-1'],
      blurbAr: 'خط COB للمقارنة والجودة العالية (~100 م).',
      image: '/shop/products/led-strip-cob.png',
      status: 'pi',
      badgeAr: 'على PI · 100 م',
      qtyLabel: '100 م',
      piRef: '0260924',
    },

    // —— Switches NaLite ——
    {
      id: 'nalite-1g',
      category: 'switches',
      nameAr: 'مفتاح حائط زجاج 1 جانغ',
      nameEn: '1-Gang Glass Wall Switch',
      model: 'NaLite X39UAG 1G',
      vendor: 'NaLite',
      protocol: ['Tuya Zigbee N&L'],
      blurbAr: 'سلسلة زجاج فاخرة 86×86 مم — خط مشاريع ومعرض.',
      image: '/shop/products/switch-1gang.png',
      status: 'pi',
      badgeAr: 'على PI · 30 قطعة',
      qtyLabel: '30 pcs',
      piRef: 'N26090721',
    },
    {
      id: 'nalite-2g',
      category: 'switches',
      nameAr: 'مفتاح حائط زجاج 2 جانغ',
      nameEn: '2-Gang Glass Wall Switch',
      model: 'NaLite X39UAG 2G',
      vendor: 'NaLite',
      protocol: ['Tuya Zigbee N&L'],
      blurbAr: 'نفس سلسلة X39 — أبيض/أسود حسب التوزيع على PI.',
      image: '/shop/products/switch-2gang.png',
      status: 'pi',
      badgeAr: 'على PI · 30 قطعة',
      qtyLabel: '30 pcs',
      piRef: 'N26090721',
    },
    {
      id: 'nalite-3g',
      category: 'switches',
      nameAr: 'مفتاح حائط زجاج 3 جانغ',
      nameEn: '3-Gang Glass Wall Switch',
      model: 'NaLite X39UAG 3G',
      vendor: 'NaLite',
      protocol: ['Tuya Zigbee N&L'],
      blurbAr: 'للغرف متعددة الدارات ضمن خط الزجاج الفاخر.',
      image: '/shop/products/switch-3gang.png',
      status: 'pi',
      badgeAr: 'على PI · 25 قطعة',
      qtyLabel: '25 pcs',
      piRef: 'N26090721',
    },
    {
      id: 'nalite-scene',
      category: 'switches',
      nameAr: 'مفتاح مشاهد 4 أزرار',
      nameEn: '4-Scene Switch',
      model: 'NaLite X39UAG Scene',
      vendor: 'NaLite',
      protocol: ['Tuya Zigbee N&L'],
      blurbAr: 'مشاهد جاهزة للمعرض والمنازل الذكية.',
      image: '/shop/products/switch-scene.png',
      status: 'pi',
      badgeAr: 'على PI · 7 قطع',
      qtyLabel: '7 pcs',
      piRef: 'N26090721',
    },
    {
      id: 'nalite-curtain-sw',
      category: 'switches',
      nameAr: 'مفتاح ستارة (محرك واحد)',
      nameEn: 'Curtain Switch 1 Motor',
      model: 'NaLite X39UAG Curtain',
      vendor: 'NaLite',
      protocol: ['Tuya Zigbee N&L'],
      blurbAr: 'يعمل مع محركات غير ذكية عبر مفتاح الحائط.',
      image: '/shop/products/switch-curtain.png',
      status: 'pi',
      badgeAr: 'على PI · 7 قطع',
      qtyLabel: '7 pcs',
      piRef: 'N26090721',
    },
    {
      id: 'nalite-dimmer',
      category: 'switches',
      nameAr: 'ديمر + 3 مشاهد',
      nameEn: 'Dimmer + 3 Scene',
      model: 'NaLite X31/X39 Dimmer',
      vendor: 'NaLite',
      protocol: ['Tuya Zigbee N&L'],
      blurbAr: 'تخفيت MOSFET مع مشاهد على نفس اللوحة.',
      image: '/shop/products/switch-dimmer.png',
      status: 'pi',
      badgeAr: 'على PI · 6 قطع',
      qtyLabel: '6 pcs',
      piRef: 'N26090721',
    },
    {
      id: 'nalite-socket',
      category: 'switches',
      nameAr: 'مقبس عالمي + USB + Type-C 20W',
      nameEn: 'Universal Socket USB-C 20W',
      model: 'NaLite X31/X39 Socket',
      vendor: 'NaLite',
      protocol: ['Tuya Zigbee N&L'],
      blurbAr: 'مقبس زجاجي مع شحن سريع Type-C.',
      image: '/shop/products/socket-usb.png',
      status: 'pi',
      badgeAr: 'على PI · 25 قطعة',
      qtyLabel: '25 pcs',
      piRef: 'N26090721',
    },

    // —— SIXWGH ——
    {
      id: 'six-whdz01',
      category: 'switches',
      nameAr: 'مقبس EU ذكي SIXWGH',
      nameEn: 'EU Smart Socket',
      model: 'WHDZ01',
      vendor: 'SIXWGH',
      protocol: ['Zigbee'],
      blurbAr: 'مقبس سعر أوسط لمخزون التجزئة.',
      image: '/shop/products/socket-eu.png',
      status: 'pi',
      badgeAr: 'على PI · 50 قطعة',
      qtyLabel: '50 pcs',
      piRef: 'WEN20260918',
    },
    {
      id: 'six-1g-zb',
      category: 'switches',
      nameAr: 'مفتاح 1 جانغ Zigbee SIXWGH',
      nameEn: '1-Gang Zigbee Switch',
      model: 'WHX01 Zigbee',
      vendor: 'SIXWGH',
      protocol: ['Zigbee'],
      blurbAr: 'خط سعر أوسط — الكمية الأساسية للمنازل.',
      image: '/shop/products/switch-1gang.png',
      status: 'pi',
      badgeAr: 'على PI · 50 قطعة',
      qtyLabel: '50 pcs',
      piRef: 'WEN20260918',
    },
    {
      id: 'six-1g-wifi',
      category: 'switches',
      nameAr: 'مفتاح 1 جانغ WiFi (عينة مقارنة)',
      nameEn: '1-Gang WiFi Sample',
      model: 'WHX01 WiFi',
      vendor: 'SIXWGH',
      protocol: ['WiFi', 'Tuya'],
      blurbAr: 'عينات مقارنة فقط بجانب خط Zigbee.',
      image: '/shop/products/switch-1gang.png',
      status: 'pi',
      badgeAr: 'على PI · 5 قطع',
      qtyLabel: '5 pcs',
      piRef: 'WEN20260918',
    },
    {
      id: 'six-2g-zb',
      category: 'switches',
      nameAr: 'مفتاح 2 جانغ Zigbee SIXWGH',
      nameEn: '2-Gang Zigbee Switch',
      model: 'WHX02 Zigbee',
      vendor: 'SIXWGH',
      protocol: ['Zigbee'],
      blurbAr: 'مخزون أوسط للمنازل والمحال.',
      image: '/shop/products/switch-2gang.png',
      status: 'pi',
      badgeAr: 'على PI · 50 قطعة',
      qtyLabel: '50 pcs',
      piRef: 'WEN20260918',
    },
    {
      id: 'six-2g-wifi',
      category: 'switches',
      nameAr: 'مفتاح 2 جانغ WiFi (عينة مقارنة)',
      nameEn: '2-Gang WiFi Sample',
      model: 'WHX02 WiFi',
      vendor: 'SIXWGH',
      protocol: ['WiFi', 'Tuya'],
      blurbAr: 'عينات مقارنة WiFi.',
      image: '/shop/products/switch-2gang.png',
      status: 'pi',
      badgeAr: 'على PI · 5 قطع',
      qtyLabel: '5 pcs',
      piRef: 'WEN20260918',
    },
    {
      id: 'six-3g-zb',
      category: 'switches',
      nameAr: 'مفتاح 3 جانغ Zigbee SIXWGH',
      nameEn: '3-Gang Zigbee Switch',
      model: 'WHX03 Zigbee',
      vendor: 'SIXWGH',
      protocol: ['Zigbee'],
      blurbAr: 'ثلاث دارات بسعر أوسط.',
      image: '/shop/products/switch-3gang.png',
      status: 'pi',
      badgeAr: 'على PI · 40 قطعة',
      qtyLabel: '40 pcs',
      piRef: 'WEN20260918',
    },

    // —— Sensors MIRSZ ——
    {
      id: 'mir-ir600',
      category: 'sensors',
      nameAr: 'حساس حركة PIR',
      nameEn: 'PIR Motion Detector',
      model: 'MIR-IR600-TY',
      vendor: 'MIRSZ',
      protocol: ['Zigbee'],
      blurbAr: 'كشف حركة 8م / 90° — بطارية CR2450.',
      image: '/shop/products/sensor-pir.png',
      status: 'pi',
      badgeAr: 'على PI · 29 قطعة',
      qtyLabel: '29 pcs',
      piRef: 'MIR-202609181362',
    },
    {
      id: 'mir-ir100',
      category: 'sensors',
      nameAr: 'حساس حركة PIR دوّار',
      nameEn: 'Rotating PIR Detector',
      model: 'MIR-IR100-ZT5',
      vendor: 'MIRSZ',
      protocol: ['Zigbee'],
      blurbAr: 'دوران 360° مع زاوية كشف 90°.',
      image: '/shop/products/sensor-pir.png',
      status: 'pi',
      badgeAr: 'على PI · 10 قطع',
      qtyLabel: '10 pcs',
      piRef: 'MIR-202609181362',
    },
    {
      id: 'mir-mc100',
      category: 'sensors',
      nameAr: 'حساس باب/نافذة',
      nameEn: 'Door/Window Sensor',
      model: 'MIR-MC100-ZT5',
      vendor: 'MIRSZ',
      protocol: ['Zigbee'],
      blurbAr: 'مغناطيسي مع حماية فتح الغطاء.',
      image: '/shop/products/sensor-door.png',
      status: 'pi',
      badgeAr: 'على PI · 50 قطعة',
      qtyLabel: '50 pcs',
      piRef: 'MIR-202609181362',
    },
    {
      id: 'mir-sm100',
      category: 'sensors',
      nameAr: 'كاشف دخان',
      nameEn: 'Smoke Detector',
      model: 'MIR-SM100',
      vendor: 'MIRSZ',
      protocol: ['Zigbee'],
      blurbAr: 'كاشف ضوئي مع إنذار صوتي >85dB.',
      image: '/shop/products/sensor-smoke.png',
      status: 'pi',
      badgeAr: 'على PI · 30 قطعة',
      qtyLabel: '30 pcs',
      piRef: 'MIR-202609181362',
    },
    {
      id: 'mir-wa100',
      category: 'sensors',
      nameAr: 'كاشف تسرب ماء',
      nameEn: 'Water Leak Detector',
      model: 'MIR-WA100-ZT5',
      vendor: 'MIRSZ',
      protocol: ['Zigbee'],
      blurbAr: 'IP66 للمطابخ والحمامات.',
      image: '/shop/products/sensor-water.png',
      status: 'pi',
      badgeAr: 'على PI · 20 قطعة',
      qtyLabel: '20 pcs',
      piRef: 'MIR-202609181362',
    },
    {
      id: 'mir-ga100',
      category: 'sensors',
      nameAr: 'كاشف غاز طبيعي',
      nameEn: 'Natural Gas Detector',
      model: 'MIR-GA100',
      vendor: 'MIRSZ',
      protocol: ['Zigbee'],
      blurbAr: 'كشف CH4 مع محول 12V.',
      image: '/shop/products/sensor-gas.png',
      status: 'pi',
      badgeAr: 'على PI · 15 قطعة',
      qtyLabel: '15 pcs',
      piRef: 'MIR-202609181362',
    },

    // —— Curtains ——
    {
      id: 'galime-x20',
      category: 'motors',
      nameAr: 'طقم ستارة ذكية Galime X20',
      nameEn: 'Smart Curtain Kit 4.2m',
      model: 'TB2604 X20 + مسار 4.2م',
      vendor: 'Galime',
      protocol: ['Zigbee'],
      blurbAr: 'محرك + مسار ألمنيوم + إكسسوارات + ريموت — عينة أولى.',
      image: '/shop/products/curtain-kit.png',
      status: 'pi',
      badgeAr: 'عينة على PI · 1 طقم',
      qtyLabel: '1 set',
      piRef: 'GM20260917',
    },
    {
      id: 'galime-gw',
      category: 'control',
      nameAr: 'بوابة Galime متعددة الأوضاع',
      nameEn: 'Galime Multi-mode Gateway',
      model: 'Smart Multi-mode Gateway',
      vendor: 'Galime',
      protocol: ['WiFi', 'Zigbee'],
      blurbAr: 'عينة بوابة مع طقم الستارة.',
      image: '/shop/products/galime-gateway.png',
      status: 'pi',
      badgeAr: 'عينة على PI · 1',
      qtyLabel: '1 pcs',
      piRef: 'GM20260917',
    },
    {
      id: 'longsam-sample',
      category: 'motors',
      nameAr: 'عينة محرك ستارة LONGSAM',
      nameEn: 'LONGSAM Curtain Motor Sample',
      model: 'LS82TYZ Zigbee + LS82TYW WiFi + مسارات تلسكوبية',
      vendor: 'LONGSAM',
      protocol: ['Tuya Zigbee', 'Tuya WiFi'],
      blurbAr: 'طقم عينة سلسلة 82: محرك Zigbee + محرك WiFi + ريموتات RF + مسارين تلسكوبيين (1.8–3م و2.3–4م) — بانتظار اعتماد الدفع.',
      image: '/shop/products/longsam-ls82-catalog.png',
      status: 'requested',
      badgeAr: 'طلب عينة · بانتظار الدفع',
      qtyLabel: '1 طقم عينة (~$91)',
    },

    // —— Gateways / control ——
    {
      id: 'bsum-gw',
      category: 'control',
      nameAr: 'بوابة BSUMWELL متعددة الأوضاع',
      nameEn: 'Multi-mode Zigbee Gateway',
      model: 'BSW-GWBL005',
      vendor: 'BSUMWELL',
      protocol: ['Zigbee', 'BT', 'WiFi'],
      blurbAr: 'بوابة مع خط لمبات BSUMWELL.',
      image: '/shop/products/gateway-zigbee.png',
      status: 'pi',
      badgeAr: 'على PI · 8 قطع',
      qtyLabel: '8 pcs',
      piRef: '12026092402',
    },
    {
      id: 'nalite-gw',
      category: 'control',
      nameAr: 'بوابة NaLite Multi-Mode',
      nameEn: 'NaLite Multi-Mode Gateway',
      model: 'Smart Multi-Mode Gateway',
      vendor: 'NaLite',
      protocol: ['WiFi', 'Zigbee', 'BLE', 'Thread'],
      blurbAr: 'بوابة للمعرض وخط الزجاج الفاخر.',
      image: '/shop/products/gateway-zigbee.png',
      status: 'pi',
      badgeAr: 'على PI · 5 قطع',
      qtyLabel: '5 pcs',
      piRef: 'N26090721',
    },
    {
      id: 'owon-ac221',
      category: 'control',
      nameAr: 'تحكم مكيّف سبليت OWON',
      nameEn: 'Zigbee Mini-Split AC Controller',
      model: 'AC 221-Z',
      vendor: 'OWON',
      protocol: ['Zigbee (غير Tuya)'],
      blurbAr: 'يسد فجوة التكييف السبليت في سوريا — طلب إصدار PI.',
      image: '/shop/products/owon-ac221.png',
      status: 'requested',
      badgeAr: 'مطلوب PI · 20 قطعة',
      qtyLabel: '20 pcs',
    },
    {
      id: 'owon-segx3',
      category: 'control',
      nameAr: 'بوابة OWON SEG-X3',
      nameEn: 'OWON Zigbee Gateway',
      model: 'SEG X3-Z-W',
      vendor: 'OWON',
      protocol: ['Zigbee', 'WiFi'],
      blurbAr: 'بوابة لتشغيل خط OWON HVAC محلياً.',
      image: '/shop/products/owon-seg-x3.png',
      status: 'requested',
      badgeAr: 'مطلوب PI · 8 قطع',
      qtyLabel: '8 pcs',
    },
    {
      id: 'owon-cb432',
      category: 'control',
      nameAr: 'ريليه/قاطع DIN مع قياس',
      nameEn: 'DIN Smart Relay / Breaker',
      model: 'CB432',
      vendor: 'OWON',
      protocol: ['Zigbee'],
      blurbAr: 'وحدات لوحة كهرباء ذكية — تأكيد تيار 16/32/63A على PI.',
      image: '/shop/products/owon-cb432.png',
      status: 'requested',
      badgeAr: 'مطلوب PI · 15 قطعة',
      qtyLabel: '15 pcs',
    },
    {
      id: 'owon-pct503',
      category: 'control',
      nameAr: 'ترموستات جداري OWON',
      nameEn: 'Zigbee Wall Thermostat',
      model: 'PCT 503',
      vendor: 'OWON',
      protocol: ['Zigbee'],
      blurbAr: 'عينات معرض/مشاريع — أو بديل أقرب إن توقف الموديل.',
      image: '/shop/products/owon-pct503.png',
      status: 'requested',
      badgeAr: 'مطلوب PI · 5 قطع',
      qtyLabel: '5 pcs',
    },
    {
      id: 'nalite-vrf',
      category: 'control',
      nameAr: 'ترموستات VRF NaLite',
      nameEn: 'VRF Thermostat',
      model: 'NT01-VRF',
      vendor: 'NaLite',
      protocol: ['Tuya Zigbee'],
      blurbAr: 'كان على PI العينة 17/9 — غير مدرج في PI المخزون الحالي؛ متاح عند الطلب.',
      image: '/shop/products/nalite-nt01-vrf.png',
      status: 'inquiry',
      badgeAr: 'عند الطلب',
      qtyLabel: 'حسب الطلب',
    },

    // —— Locks ——
    {
      id: 'vtk-ad02',
      category: 'locks',
      nameAr: 'قفل ذكي AD02',
      nameEn: 'Smart Lock AD02',
      model: 'AD02',
      vendor: 'VANTEK',
      protocol: ['Tuya'],
      blurbAr: 'كيباد + مفتاح + تطبيق + بطاقة.',
      image: '/shop/products/lock-ad02.png',
      status: 'requested',
      badgeAr: 'طلب مخزون · 25 قطعة',
      qtyLabel: '25 pcs',
      piRef: 'FAPI260918017',
    },
    {
      id: 'vtk-ad05',
      category: 'locks',
      nameAr: 'قفل ذكي AD05',
      nameEn: 'Smart Lock AD05',
      model: 'AD05',
      vendor: 'VANTEK',
      protocol: ['Tuya'],
      blurbAr: 'بصمة + كلمة مرور + مفتاح + تطبيق.',
      image: '/shop/products/lock-ad05.png',
      status: 'requested',
      badgeAr: 'طلب مخزون · 25 قطعة',
      qtyLabel: '25 pcs',
      piRef: 'FAPI260918017',
    },
    {
      id: 'vtk-at01',
      category: 'locks',
      nameAr: 'قفل ذكي AT01',
      nameEn: 'Smart Lock AT01',
      model: 'AT01',
      vendor: 'VANTEK',
      protocol: ['Tuya'],
      blurbAr: 'بصمة + كلمة + مفتاح + تطبيق.',
      image: '/shop/products/lock-at01.png',
      status: 'requested',
      badgeAr: 'طلب مخزون · 25 قطعة',
      qtyLabel: '25 pcs',
      piRef: 'FAPI260918017',
    },
    {
      id: 'vtk-al02',
      category: 'locks',
      nameAr: 'قفل ذكي AL02',
      nameEn: 'Smart Lock AL02',
      model: 'AL02',
      vendor: 'VANTEK',
      protocol: ['Tuya'],
      blurbAr: 'موديل على طلب المخزون المحدّث لـ VANTEK.',
      image: '/shop/products/lock-al02.png',
      status: 'requested',
      badgeAr: 'طلب مخزون · 30 قطعة',
      qtyLabel: '30 pcs',
      piRef: 'FAPI260918017',
    },
    {
      id: 'vtk-sr01',
      category: 'locks',
      nameAr: 'قفل ذكي SR01',
      nameEn: 'Smart Lock SR01',
      model: 'SR01',
      vendor: 'VANTEK',
      protocol: ['Tuya'],
      blurbAr: 'بصمة + بطاقة + كلمة + مفتاح + تطبيق.',
      image: '/shop/products/lock-sr01.png',
      status: 'requested',
      badgeAr: 'طلب مخزون · 20 قطعة',
      qtyLabel: '20 pcs',
      piRef: 'FAPI260918017',
    },
    {
      id: 'vtk-g2',
      category: 'locks',
      nameAr: 'قفل ذكي G2',
      nameEn: 'Smart Lock G2',
      model: 'G2',
      vendor: 'VANTEK',
      protocol: ['Tuya'],
      blurbAr: 'موديل أطول للأبواب السميكة (50–100 مم).',
      image: '/shop/products/lock-g2.png',
      status: 'requested',
      badgeAr: 'طلب مخزون · 15 قطعة',
      qtyLabel: '15 pcs',
      piRef: 'FAPI260918017',
    },
    {
      id: 'wafu-locks',
      category: 'locks',
      nameAr: 'أقفال WAFU (من الكتالوج)',
      nameEn: 'WAFU Smart Locks Catalog',
      model: 'WAFU Catalog 2026',
      vendor: 'WAFU',
      protocol: ['Tuya'],
      blurbAr: 'طلب مخزون أول: أفضل موديلين × 8 قطع، وموديل بسعر مختلف × 6 قطع.',
      image: '/shop/products/wafu-lock.png',
      status: 'requested',
      badgeAr: 'طلب مخزون · 8+8+6',
      qtyLabel: '8+8+6 pcs',
    },

    // —— Cleaning ——
    {
      id: 'abir-vac',
      category: 'cleaning',
      nameAr: 'روبوت كنس ABIR',
      nameEn: 'ABIR Robot Vacuum',
      model: 'M1PRO / K30 / X9',
      vendor: 'ABIR / Xinsu',
      protocol: ['Tuya APP'],
      blurbAr: 'طلب عينات 3 قطع لكل موديل يوصي به المصنع — بانتظار PI.',
      image: '/shop/products/abir-m1pro.png',
      status: 'requested',
      badgeAr: 'مطلوب عينات',
      qtyLabel: '3/موديل',
    },
    {
      id: 'abir-window',
      category: 'cleaning',
      nameAr: 'روبوت نوافذ ABIR',
      nameEn: 'Window Cleaning Robot',
      model: 'WD8 / WD9 / WD10',
      vendor: 'ABIR / Xinsu',
      protocol: ['Tuya APP'],
      blurbAr: 'روبوت مسح نوافذ — 3 قطع لكل موديل يوصي به المصنع.',
      image: '/shop/products/abir-wd-window.png',
      status: 'requested',
      badgeAr: 'مطلوب عينات',
      qtyLabel: '3/موديل',
    },
    {
      id: 'abir-stick',
      category: 'cleaning',
      nameAr: 'مكنسة يدوية/عصا',
      nameEn: 'Cordless Stick Vacuum',
      model: 'حسب توصية المصنع',
      vendor: 'ABIR / Xinsu',
      protocol: ['غالباً بدون Tuya'],
      blurbAr: 'طلب عينات 3 قطع لكل موديل عصا يوصي به المصنع.',
      image: '/shop/products/abir-stick-vacuum.png',
      status: 'requested',
      badgeAr: 'مطلوب عينات',
      qtyLabel: '3/موديل',
    },

    // —— Panels / cameras ——
    {
      id: 'portworld-panel',
      category: 'panels',
      nameAr: 'لوحة لمس Portworld',
      nameEn: 'Smart Touch Panel',
      model: 'YC-SM08P / SM41P / SM10',
      vendor: 'Portworld',
      protocol: ['Android panel'],
      blurbAr: 'طلب مخزون أول: SM08P × 6، SM41P × 8، SM10 × 4، وبوابة اختيارية × 5.',
      image: '/shop/products/portworld-panel.png',
      status: 'requested',
      badgeAr: 'طلب مخزون · 6+8+4',
      qtyLabel: '6+8+4 pcs',
    },
    {
      id: 'smatek-line',
      category: 'panels',
      nameAr: 'خط Smatek للمنزل الذكي',
      nameEn: 'Smatek Smart Home Line',
      model: 'Catalog 2026',
      vendor: 'Smatek',
      protocol: ['Zigbee / مراجعة Tuya'],
      blurbAr: 'طلب مخزون متوافق مع سوريا: مفاتيح 30/30/20، مقابس 20، بوابة 8. دعم Tuya يحتاج اختبار.',
      image: '/shop/products/smatek-line.png',
      status: 'requested',
      badgeAr: 'طلب مخزون · 30/30/20',
      qtyLabel: '30/30/20 + 20 + 8',
    },
    {
      id: 'sinovision-cam',
      category: 'panels',
      nameAr: 'كاميرات / جرس فيديو',
      nameEn: 'Cameras & Video Doorbell',
      model: 'Sinovision RFQ',
      vendor: 'Sinovision',
      protocol: ['Tuya / WiFi'],
      blurbAr: 'طلب مخزون أول: W5×20، W16×10، B2×15، جرس D5×15، P1×10.',
      image: '/shop/products/sinovision-camera.png',
      status: 'requested',
      badgeAr: 'طلب مخزون · 70 قطعة',
      qtyLabel: '70 pcs',
    },
  ],
};

export function whatsappOrderUrl(product: ShopProduct, phone: string): string {
  const text = [
    'مرحباً الشراع — طلب من صفحة المنزل الذكي',
    '',
    `المنتج: ${product.nameAr}`,
    `الموديل: ${product.model}`,
    `المورد: ${product.vendor}`,
    product.qtyLabel ? `الكمية المرجعية: ${product.qtyLabel}` : '',
    product.piRef ? `مرجع PI: ${product.piRef}` : '',
    '',
    'أريد معرفة السعر بالليرة/الدولار والتوفر وكيفية الطلب.',
  ]
    .filter(Boolean)
    .join('\n');
  return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
}
